/*
	OneSignal push, for the Capacitor shell (see mobile/PUSH-SETUP.md). The app runs in the shell
	from its live URL, so the native plugin is never imported here: it is read from the global the
	shell injects (onesignal-cordova-plugin). In a normal browser every function is a silent no-op,
	and none of them ever throws.

	The OneSignal external id is the Mango member id (the string in localStorage.user).
*/

const ASKED_KEY = 'pushPrompted'

let initialized = false
let wantedId = null // member the app is signed in as
let linkedId = null // member OneSignal currently knows

function plugin() {
	const g = globalThis
	if (!g.Capacitor?.isNativePlatform?.()) return null
	return g.plugins?.OneSignal || g.OneSignal || null
}

export function isNativePush() {
	return !!plugin()
}

function configured(appId) {
	return typeof appId === 'string' && appId.trim() !== '' && !appId.startsWith('YOUR-')
}

function syncLink() {
	const OneSignal = plugin()
	if (!OneSignal || !initialized) return
	try {
		if (wantedId && wantedId !== linkedId) {
			OneSignal.login(wantedId)
			linkedId = wantedId
		} else if (!wantedId && linkedId) {
			OneSignal.logout()
			linkedId = null
		}
	} catch {
		// push must never break sign-in or sign-out
	}
}

export function initPush(appId) {
	if (initialized) return
	const OneSignal = plugin()
	if (!OneSignal || !configured(appId)) return
	try {
		OneSignal.initialize(appId)
		initialized = true
	} catch {
		return
	}
	syncLink()
}

// Sign-in or session restore. Safe to call before initPush and repeatedly.
export function linkPushUser(memberId) {
	wantedId = memberId ? String(memberId) : null
	syncLink()
}

// Sign-out or a forced session expiry, so the next person on the phone never gets this member's pushes.
export function unlinkPushUser() {
	wantedId = null
	syncLink()
}

function storage() {
	try {
		return globalThis.localStorage || null
	} catch {
		return null
	}
}

function markAsked() {
	try {
		storage()?.setItem(ASKED_KEY, '1')
	} catch {
		// private mode
	}
}

export function wasPushAsked() {
	try {
		return storage()?.getItem(ASKED_KEY) === '1'
	} catch {
		return false
	}
}

export async function isPushGranted() {
	const OneSignal = plugin()
	if (!OneSignal || !initialized) return false
	try {
		return !!(await OneSignal.Notifications.getPermissionAsync())
	} catch {
		return false
	}
}

// True when it is worth showing our own explainer: native, set up, not yet asked, not already on.
export async function shouldOfferPush() {
	if (!isNativePush() || !initialized || wasPushAsked()) return false
	return !(await isPushGranted())
}

// The system prompt (iOS shows it once; later this opens the phone's notification settings).
export async function requestPushPermission() {
	const OneSignal = plugin()
	if (!OneSignal || !initialized) return false
	markAsked()
	try {
		return !!(await OneSignal.Notifications.requestPermission(true))
	} catch {
		return false
	}
}

// The shopper said "Not now" to our explainer; don't offer it again after every order.
export function declinePushOffer() {
	markAsked()
}

// Test hook.
export function resetPushForTests() {
	initialized = false
	wantedId = null
	linkedId = null
}
